/**
 * 应用侧边栏组件
 * 基于 shadcn/ui Sidebar 组件
 */

import { Link, useLocation } from 'react-router-dom'
import { ROUTES } from '@/constants/routes'
import {
  Home,
  FileText,
  History,
  BookOpen,
  BrainCircuit,
  Stethoscope,
  GraduationCap,
  CheckSquare,
  Presentation,
  MessageSquare,
  Shapes,
  Paintbrush,
  Network,
  Wrench,
  List,
  Target,
  ChevronRight,
  LogOut,
  User,
} from 'lucide-react'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/store/authStore'
import { useNavigate } from 'react-router-dom'
import { NAV_ITEMS, type NavItem } from '@/constants/navigation'
import { isPathActive } from '@/hooks/useBreadcrumb'

// 图标映射
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Home,
  FileText,
  History,
  BookOpen,
  BrainCircuit,
  Stethoscope,
  GraduationCap,
  CheckSquare,
  Presentation,
  MessageSquare,
  Shapes,
  Paintbrush,
  Network,
  Wrench,
  List,
  Target,
}

function getIcon(name: string) {
  return ICON_MAP[name] || Home
}

interface NavItemComponentProps {
  item: NavItem
  currentPath: string
}

function NavItemComponent({ item, currentPath }: NavItemComponentProps) {
  const Icon = getIcon(item.icon)
  const hasChildren = item.children && item.children.length > 0
  // 获取第一个子项的路径作为父项的跳转目标
  const firstChildPath = hasChildren ? item.children?.[0]?.path : undefined
  const targetPath = item.path || firstChildPath || '#'
  const isActive = targetPath !== '#' ? isPathActive(currentPath, targetPath) : false
  // 检查是否有任何子项处于激活状态
  const hasActiveChild = hasChildren && item.children?.some(child =>
    child.path ? isPathActive(currentPath, child.path) : false
  )
  // 是否应该展开（响应路由变化）
  const shouldOpen = isActive || hasActiveChild

  // 如果有子菜单
  if (hasChildren) {
    return (
      <Collapsible open={shouldOpen} className="group/collapsible">
        <SidebarMenuItem>
          <CollapsibleTrigger asChild>
            <SidebarMenuButton asChild isActive={shouldOpen}>
              <Link to={targetPath}>
                <Icon className="h-4 w-4" />
                <span>{item.label}</span>
                <ChevronRight className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
              </Link>
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>
              {item.children?.map((child) => (
                <NavSubItem key={child.label} item={child} currentPath={currentPath} />
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    )
  }

  // 如果是普通菜单项
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isActive}>
        <Link to={targetPath}>
          <Icon className="h-4 w-4" />
          <span>{item.label}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

interface NavSubItemProps {
  item: NavItem
  currentPath: string
}

function NavSubItem({ item, currentPath }: NavSubItemProps) {
  const Icon = getIcon(item.icon)
  const isActive = item.path ? isPathActive(currentPath, item.path) : false

  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton asChild isActive={isActive}>
        <Link to={item.path || '#'}>
          <Icon className="h-4 w-4" />
          <span>{item.label}</span>
        </Link>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  )
}

export function AppSidebar() {
  const location = useLocation()
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const isStudent = user?.role === 'student'

  // 根据角色过滤菜单项
  const filteredNavItems = NAV_ITEMS.filter((item) => {
    if (!item.roles) return true
    return item.roles.includes(isStudent ? 'student' : 'teacher')
  })

  const handleLogout = () => {
    logout()
    navigate(ROUTES.LOGIN)
  }

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to={ROUTES.HOME}>
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <Home className="size-4" />
                </div>
                <div className="flex flex-col gap-0.5 leading-none">
                  <span className="font-semibold">AI Tutor</span>
                  <span className="text-xs text-muted-foreground">智能辅导平台</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>导航</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {filteredNavItems.map((item) => (
                <NavItemComponent
                  key={item.label}
                  item={item}
                  currentPath={location.pathname}
                />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg">
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-muted">
                <User className="size-4" />
              </div>
              <div className="flex flex-col gap-0.5 leading-none">
                <span className="font-medium text-sm">{user?.username || '用户'}</span>
                <span className="text-xs text-muted-foreground">
                  {isStudent ? '学生' : '教师'}
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="ml-auto"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
